import React, { useCallback, useEffect, useState } from 'react';
import {
  Space,
  Typography,
  Card,
  Row,
  Col,
  Descriptions,
  Tag,
  Statistic,
  Divider,
  Table,
  Button
} from 'antd';
import {
  UserOutlined,
  BankOutlined,
  TrophyOutlined,
  DollarOutlined,
  FilePdfOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  StarOutlined,
  ShoppingOutlined,
  GiftOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { CustomerApi } from 'src/api';
import { ICustomer, ICustomerPartnerBonus } from 'main/interfaces';
import { Formatter } from 'src/helpers';
import { CustomerOrders, CustomerPartnerOrders } from '../';
import { WarehouseLots } from 'src/apps/warehouse/components';

interface ICustomerViewProps {
  customer_id: string;
}

export const CustomerView: React.FC<ICustomerViewProps> = ({ customer_id }) => {
  const [customer, setCustomer] = useState<ICustomer>();
  const [partnerBonus, setPartnerBonus] = useState<Record<string, unknown>>();
  const [partnerBonuses, setPartnerBonuses] = useState<ICustomerPartnerBonus[]>([]);

  const getCustomer = useCallback((): void => {
    CustomerApi.getOne(customer_id).then((res) => {
      setCustomer(res?.data);
    });
  }, [customer_id]);

  const getPartnerBonus = useCallback((): void => {
    if (customer?.partner) {
      CustomerApi.getPartnerRangAndBonus(customer_id).then((res) => {
        setPartnerBonus(res?.data);
      });
    }
  }, [customer_id, customer?.partner]);

  const getPartnerBonuses = useCallback((): void => {
    console.log('Customer partner status:', customer?.partner);
    if (customer?.partner) {
      CustomerApi.getCustomerPartnerBonuses(customer_id).then((res) => {
        console.log('Customer partner bonuses response:', res);
        setPartnerBonuses(res?.data || []);
      });
    }
  }, [customer_id, customer?.partner]);

  useEffect(() => {
    if (!customer) {
      getCustomer();
    }
  }, [customer, getCustomer]);

  useEffect(() => {
    if (customer?.partner) {
      getPartnerBonus();
      getPartnerBonuses();
    }
  }, [customer, getPartnerBonus, getPartnerBonuses]);

  if (!customer) return null;

  const handleOpenDocument = (link: string | undefined) => () => {
    if (link) {
      window.open(link, '_blank');
    }
  };

  const bonusColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Бонус',
      dataIndex: 'bonus',
      key: 'bonus',
      render: (bonus: number) => Formatter.toMoney(bonus, true)
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'orange'}>
          {status === 1 ? 'Виплачено' : 'Очікує виплати'}
        </Tag>
      )
    },
    {
      title: 'Документ',
      key: 'document',
      render: (_: unknown, record: ICustomerPartnerBonus) => (
        <Button
          type="link"
          icon={<FilePdfOutlined />}
          onClick={handleOpenDocument(record.link)}
        >
          Переглянути
        </Button>
      )
    }
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Основна інформація */}
      <Card
        title={
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            <span>Основна інформація</span>
          </Space>
        }
        style={{ backgroundColor: '#fafafa' }}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Descriptions title="Особисті дані" bordered>
              <Descriptions.Item label="ПІП" span={3}>
                {customer?.info ? `${customer.info.surname} ${customer.info.name} ${customer.info.middle_name || ''}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={3}>
                {customer?.info?.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Телефон" span={3}>
                {customer?.info?.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Дата реєстрації" span={3}>
                {Formatter.toDate(customer?.createdAt || '')}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions title="Статус" bordered>
              <Descriptions.Item label="Партнер" span={3}>
                {customer?.partner ? (
                  <Tag color="green" icon={<CheckCircleFilled />}>
                    Так
                  </Tag>
                ) : (
                  <Tag color="red" icon={<CloseCircleFilled />}>
                    Ні
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Активний" span={3}>
                {customer?.active ? (
                  <Tag color="green" icon={<CheckCircleFilled />}>
                    Активний
                  </Tag>
                ) : (
                  <Tag color="red" icon={<CloseCircleFilled />}>
                    Неактивний
                  </Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* Інформація про партнера */}
      {customer?.partner_info && (
        <Card
          title={
            <Space>
              <TrophyOutlined style={{ color: '#52c41a' }} />
              <span>Дані партнера</span>
            </Space>
          }
          style={{ backgroundColor: '#f6ffed' }}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Descriptions title="Особисті дані партнера" bordered>
                <Descriptions.Item label="Адреса реєстрації" span={3}>
                  {customer.partner_info.address_register}
                </Descriptions.Item>
                <Descriptions.Item label="Серія паспорта" span={3}>
                  {customer.partner_info.pasport_series}
                </Descriptions.Item>
                <Descriptions.Item label="Орган видачі" span={3}>
                  {customer.partner_info.pasport_organization}
                </Descriptions.Item>
                <Descriptions.Item label="Дата видачі" span={3}>
                  {Formatter.toDate(customer.partner_info.pasport_data)}
                </Descriptions.Item>
                <Descriptions.Item label="ІПН" span={3}>
                  {customer.partner_info.ipn}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions title="Банківські дані" bordered>
                <Descriptions.Item label="Банк" span={3}>
                  {customer.partner_info.bank_name}
                </Descriptions.Item>
                <Descriptions.Item label="Код банку" span={3}>
                  {customer.partner_info.bank_code}
                </Descriptions.Item>
                <Descriptions.Item label="IBAN" span={3}>
                  {customer.partner_info.bank_iban}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>
      )}

      {/* Інформація про компанію */}
      {customer?.company && (
        <Card
          title={
            <Space>
              <BankOutlined style={{ color: '#722ed1' }} />
              <span>Дані про компанію</span>
            </Space>
          }
          style={{ backgroundColor: '#f9f0ff' }}
        >
          <Descriptions title="Інформація про компанію" bordered>
            <Descriptions.Item label="Назва компанії" span={3}>
              {customer.company.name}
            </Descriptions.Item>
            <Descriptions.Item label="Код ЄДРПОУ" span={3}>
              {customer.company.code}
            </Descriptions.Item>
            <Descriptions.Item label="ПДВ код" span={3}>
              {customer.company.pdv_code}
            </Descriptions.Item>
            <Descriptions.Item label="Адреса" span={3}>
              {customer.company.address}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Бонуси партнера */}
      {customer?.partner && partnerBonus && (
        <Card
          title={
            <Space>
              <DollarOutlined style={{ color: '#faad14' }} />
              <span>Бонуси партнера</span>
            </Space>
          }
          style={{ backgroundColor: '#fffbe6' }}
        >
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Statistic
                title="Поточний бонус"
                value={String(partnerBonus.bonus || '')}
                prefix={<WalletOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Ранг партнера"
                value={String(partnerBonus.rang_info || '')}
                prefix={<StarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Відсоток бонусу"
                value={`${partnerBonus.rang_percent || 0}%`}
                prefix={<GiftOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Col>
          </Row>

          <Divider />

          <Typography.Title level={5}>
            <ShoppingOutlined /> Історія бонусів
          </Typography.Title>

          <Table
            columns={bonusColumns}
            dataSource={partnerBonuses}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Замовлення */}
      <Typography.Title level={4} type="secondary">
        <ShoppingOutlined /> Замовлення
      </Typography.Title>
      <CustomerOrders customer_id={customer_id} />

      {/* Партнерські замовлення */}
      {customer.partner && (
        <>
          <Typography.Title level={4} type="secondary">
            <TrophyOutlined /> Партнерські замовлення
          </Typography.Title>
          <CustomerPartnerOrders customer_id={customer_id}/>
        </>
      )}

      {/* Товари клієнта */}
      {customer?.partner && customer?.warehouse?.id_warehouse && (
        <>
          <Typography.Title level={4} type="secondary">
            <BankOutlined /> Товари клієнта
          </Typography.Title>
          <WarehouseLots warehouse_id={String(customer?.warehouse?.id_warehouse)} pageSize={10} />
        </>
      )}
    </Space>
  );
};
